import {
  FIREBASE_DEPRECATION_TOOLTIP_CONTENT,
  INTEGRATION_FORM_CONTENT,
} from 'modules/mobileApps/constants';
import {
  IntegrationOperationType,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import {
  Integration,
  IntegrationAuthType,
  IntegrationPlatform,
  IntegrationStatusBadge,
  IntegrationTableRow,
} from 'modules/mobileApps/types';
import { getIsLegacyFirebaseIntegration } from 'modules/mobileApps/utils';
import { SecondaryButton } from 'ui/button';
import { Table } from 'ui/table';
import { ColumnData } from 'ui/tableHeader';
import { colors } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import noop from 'utils/noop';
import { isEmpty } from '@iterable/utils';
import { getEnvironmentLabelByPlatform, getIntegrationOptions } from '../../utils';
import {
  RowCell,
  StyledNegativeBadge,
  StyledPositiveBadge,
  StyledTableHeader,
  StyledTableRows,
  StyledWarningBadge,
  StyledWarningIcon,
} from './IntegrationTable.styled';
import { ActionButtons, EmptyState, ExpirationLabel } from './components';

export const STATUS_BADGE: IntegrationStatusBadge = {
  ACTIVE: <StyledPositiveBadge isBackgroundTransparent>Active</StyledPositiveBadge>,
  EXPIRED: <StyledNegativeBadge>Expired</StyledNegativeBadge>,
  TEST_PUSH: <StyledWarningBadge>Test Push</StyledWarningBadge>,
};

const { firebaseJson, p8AuthKey, p12Certificate, pemCertificate } =
  INTEGRATION_FORM_CONTENT.authMethods;

export const IntegrationTable = () => {
  const { openModal } = useModalContext();
  const { appDetails, assignedIntegrations, setSelectedIntegration, setOperationType } =
    useIntegrationMetadataContext();

  const integrationOptions = getIntegrationOptions(appDetails?.platform, assignedIntegrations);

  const getTypeLabel = (
    authType: IntegrationAuthType | undefined,
    platform: IntegrationPlatform,
  ) => {
    if (platform === IntegrationPlatform.GCM) return firebaseJson.name;

    switch (authType) {
      case IntegrationAuthType.P8:
        return p8AuthKey.name;
      case IntegrationAuthType.P12:
        return p12Certificate.name;
      case IntegrationAuthType.PEM:
        return pemCertificate.name;
      default:
        /* If authType is not defined in database, then integration was created before
         * p8 support was implemented. Before authType, there was no way to differentiate
         * between p12 and PEM after they have already been uploaded to AWS, so default
         * "Type" label is set to the below format */
        return `${p12Certificate.name}/${pemCertificate.name}`;
    }
  };

  const getOsNotificationStatus = ({ androidUseOsNotifications, platform }: Integration) => {
    if (platform !== IntegrationPlatform.GCM) return 'N/A';
    return `${androidUseOsNotifications ? 'Notification messages' : 'Data notifications'}`;
  };

  const getCredentialsStatus = (integration: Integration) => {
    const { authType, notValidAfter, platform, testPushSuccess } = integration;

    if (getIsLegacyFirebaseIntegration(platform, authType))
      return (
        <Tooltip tooltipContent={FIREBASE_DEPRECATION_TOOLTIP_CONTENT}>
          <SecondaryButton
            data-test="update-legacy-firebase-integration-button"
            onClick={() => {
              setSelectedIntegration(integration);
              setOperationType(IntegrationOperationType.UPDATE);
              openModal(ModalType.ConfigureAndroidIntegrationModal, {
                authType: IntegrationAuthType.FIREBASE_JSON,
              });
            }}
          >
            <StyledWarningIcon color={colors.iconWarning} />
            Update
          </SecondaryButton>
        </Tooltip>
      );

    if (authType === IntegrationAuthType.P8 || platform === IntegrationPlatform.GCM)
      return testPushSuccess ? STATUS_BADGE.ACTIVE : STATUS_BADGE.TEST_PUSH;

    return notValidAfter ? (
      <ExpirationLabel
        badges={STATUS_BADGE}
        dateTime={notValidAfter}
        testPushSuccess={testPushSuccess}
      />
    ) : (
      <>N/A</>
    );
  };

  const getActionButtons = (integration: Integration) => (
    <ActionButtons integration={integration} />
  );

  const createColumn = (
    displayName: string,
    rowName: keyof IntegrationTableRow,
  ): ColumnData & { RowCell: any } => ({
    name: displayName,
    value: rowName,
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: any) => <RowCell data-test={`row-cell-${rowName}`}>{row[rowName]}</RowCell>,
  });
  const hasFirebase = !!assignedIntegrations[IntegrationPlatform.GCM];

  const columns = [
    ...(!hasFirebase ? [createColumn('Environment', 'environment')] : []),
    createColumn('Type', 'authTypeLabel'),
    ...(hasFirebase ? [createColumn('Name', 'integrationName')] : []),
    ...(hasFirebase ? [createColumn('Message Type', 'osNotification')] : []),
    createColumn('Devices', 'devices'),
    createColumn('Status', 'status'),
    createColumn('', 'actions'),
  ];

  const rows: IntegrationTableRow[] = integrationOptions
    .flatMap(
      ({ type }) =>
        assignedIntegrations[type]?.map((integration) => {
          const { authType, deviceCount, name, platform } = integration;
          const integrationRow: IntegrationTableRow = {
            'data-test': `table-row-${platform.toLowerCase()}`,
            id: `${platform}-${name}`,
            authType,
            authTypeLabel: getTypeLabel(authType, platform),
            environment: getEnvironmentLabelByPlatform(platform),
            integrationName: name,
            osNotification: getOsNotificationStatus(integration),
            devices: deviceCount !== undefined ? deviceCount : ('N/A' as const),
            status: getCredentialsStatus(integration),
            actions: getActionButtons(integration),
          };
          return integrationRow;
        }) || ({} as IntegrationTableRow),
    )
    .filter((row) => !isEmpty(row))
    .sort((a, b) => {
      // Prod row should appear above sandbox row
      if (a.environment < b.environment) {
        return -1;
      } else if (a.environment > b.environment) {
        return 1;
      }
      return 0;
    });

  return !isEmpty(assignedIntegrations) ? (
    <Table>
      <StyledTableHeader
        columns={columns}
        sortedBy=""
        setSortedBy={noop}
        sortDirection=""
        setSortDirection={noop}
      />
      <StyledTableRows columns={columns} rows={rows} />
    </Table>
  ) : (
    <EmptyState />
  );
};
